import { DateInput } from "@/components/common/DateInput";
import type { FieldType } from "@/schemas/digital-files";
import { formatNumberInput } from "@/utils/number";
import {
  GroupTextField,
  GroupTextFieldRow,
  Input,
  Switch,
  Textarea,
} from "@elaniintc/goes-design-system";
import { FormOptions } from "./OptionsDropdown";
import { DataCenterDataSelect } from "../../components/DataCenterDataSelect";
import { FileInput } from "@/components/common/input-file";
import axios from "@/utils/axios";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useRegistryForm } from "../contexts/RegistryFormContext";
import { DateRangeInput } from "./DateRangeField";
import {
  IdentificationField,
  type IdentificationFieldType,
} from "@/components/common/identification-field";
import { getDocumentType } from "../../utils";
import { InputPhone } from "@/components/common/input-phone";
import { DataCenterDataMultiselect } from "@/pages/digital-files/entity-registry/components/DataCenterDataMultiselect";

interface RegistryFormFieldProps {
  field: FieldType;
  value: unknown;
  optionValue: string;
  onChange: (value: unknown) => void;
  onOptionChange: (
    fieldId: string,
    option: string,
    label?: string,
    table?: string,
    isDataCenter?: boolean,
  ) => void;
}

export const RegistryFormField = ({
  field,
  value,
  optionValue,
  onChange,
  onOptionChange,
}: RegistryFormFieldProps) => {
  const [localError, setLocalError] = useState<string | undefined>();
  const { setFieldError, touchedFields, setFieldTouched } = useRegistryForm();
  const Field = getFieldByType(field, value, onChange, () =>
    setFieldTouched(field.id, true),
  );
  const [options, setOptions] = useState<
    { key: string; label: string; table?: string }[]
  >(
    field.configuration?.options_dropdown?.map((option) => ({
      key: option,
      label: option,
    })) ?? [],
  );

  // Validación en useEffect para evitar loops infinitos
  useEffect(() => {
    let error: string | undefined = undefined;

    // Validar campo requerido
    if (field.is_required) {
      if (value === undefined || value === null || value === "") {
        error = `${field.name} es obligatorio`;
      } else if (field.field_type === "URL") {
        // Para archivos
        const isValid =
          value instanceof File ||
          (Array.isArray(value) && value.length > 0) ||
          (typeof value === "object" &&
            value !== null &&
            (value as { id?: string; url?: string }).id &&
            (value as { id?: string; url?: string }).url);

        if (!isValid) {
          error = `${field.name} es obligatorio`;
        }
      } else if (
        (field.field_type === "DATE" || field.field_type === "DATETIME") &&
        field.configuration?.type === "date_range"
      ) {
        // Para date_range validar que ambos campos estén completos
        const rangeValue = value as
          | { start?: string; end?: string }
          | undefined;
        if (!rangeValue?.start || !rangeValue?.end) {
          error = `Debe seleccionar fecha de inicio y fin`;
        }
      }
    }

    // Validación de email
    if (!error && field.field_type === "EMAIL" && value) {
      const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
      const emailStr = value as string;

      if (!emailRegex.test(emailStr)) {
        error = "Correo electrónico inválido";
      } else {
        // Validar dominio
        const domain = emailStr.split("@")[1]?.toLowerCase();

        if (domain) {
          // Validar dominios permitidos
          const allowedDomains = field.configuration?.allowedDomains;
          if (
            allowedDomains &&
            Array.isArray(allowedDomains) &&
            allowedDomains.length > 0
          ) {
            const normalizedAllowed = allowedDomains.map((d) =>
              d.toLowerCase().trim().replace(/^@/, ""),
            );
            const isAllowed = normalizedAllowed.some(
              (allowed) => domain === allowed || domain.endsWith(`.${allowed}`),
            );
            if (!isAllowed) {
              error = `Solo se permiten correos de los dominios: ${normalizedAllowed.join(", ")}`;
            }
          }

          // Validar dominios no permitidos
          const noAllowedDomains = field.configuration?.noAllowedDomains;
          if (
            !error &&
            noAllowedDomains &&
            Array.isArray(noAllowedDomains) &&
            noAllowedDomains.length > 0
          ) {
            const normalizedBlocked = noAllowedDomains.map((d) =>
              d.toLowerCase().trim().replace(/^@/, ""),
            );
            const isBlocked = normalizedBlocked.some(
              (blocked) => domain === blocked || domain.endsWith(`.${blocked}`),
            );
            if (isBlocked) {
              error = `No se permiten correos de los dominios: ${normalizedBlocked.join(", ")}`;
            }
          }
        }
      }
    }

    setLocalError(error);
    setFieldError(field.id, error);
  }, [
    value,
    field.is_required,
    field.name,
    field.field_type,
    field.id,
    field.configuration?.type,
    field.configuration?.allowedDomains,
    field.configuration?.noAllowedDomains,
    setFieldError,
  ]);

  const shouldShowError = touchedFields[field.id] ? localError : undefined;

  const OptionsComponentAlign = field.configuration?.buttonPosition || "left";
  const hasDataCenterConfig =
    !!field.configuration?.options_table &&
    !!field.configuration?.options_column;
  const hasLocalOptions =
    field.configuration?.options && field.configuration?.options.length > 0;

  const shouldShowOptions =
    field.configuration?.buttonEnabled &&
    (hasLocalOptions || hasDataCenterConfig);

  // Buscar la opción seleccionada usando comparación case-insensitive
  const currentOptionValue = useMemo(() => {
    const selected = options.find(
      (opt) =>
        String(opt.key).toLowerCase() ===
        String(optionValue || "").toLowerCase(),
    );

    return (
      selected ||
      (options.length > 0
        ? options[0]
        : { key: "", label: "", table: undefined })
    );
  }, [options, optionValue]);

  useEffect(() => {
    const fetchOptionsFromDataCenter = async () => {
      if (hasDataCenterConfig) {
        try {
          const table_name = field.configuration?.options_table;
          const column_name = field.configuration?.options_column;

          if (!table_name || !column_name) return;

          const response = await axios.get(
            "/admin/v1/data-center/tables/data",
            {
              params: {
                table_name,
              },
            },
          );
          const data =
            response.data.data?.map(
              (item: { [key: string]: string | number }) => ({
                key: item.id,
                label: item[column_name],
                table: table_name,
              }),
            ) ?? [];
          setOptions(data);
        } catch (error) {
          console.error(`Error fetching data from table:`, error);
          setOptions([]);
        }
      }
    };
    fetchOptionsFromDataCenter();
  }, [
    hasDataCenterConfig,
    field.configuration?.options_table,
    field.configuration?.options_column,
  ]);

  const handleOptionSelect = useCallback(
    (option: string, table?: string) => {
      const selectedOption = options.find((opt) => opt.key === option);
      onOptionChange(
        field.id,
        option,
        selectedOption?.label,
        table,
        hasDataCenterConfig,
      );
    },
    [field.id, onOptionChange, options, hasDataCenterConfig],
  );

  const OptionsComponent = useMemo(
    () =>
      shouldShowOptions && (
        <FormOptions
          options={options}
          selectedOption={currentOptionValue}
          onOptionSelect={handleOptionSelect}
        />
      ),
    [shouldShowOptions, options, currentOptionValue, handleOptionSelect],
  );

  return (
    <GroupTextField
      key={field.id}
      label={field.name}
      required={field.is_required}
      error={shouldShowError}
    >
      <GroupTextFieldRow
        className={
          field.field_type === "TEXTAREA" ||
          field.field_type === "URL" ||
          (field.field_type === "DATE" &&
            field.configuration?.type === "date_range")
            ? "h-auto"
            : ""
        }
      >
        {OptionsComponentAlign === "left" && OptionsComponent}
        {Field}
        {OptionsComponentAlign === "right" && OptionsComponent}
      </GroupTextFieldRow>
    </GroupTextField>
  );
};

const getFieldByType = (
  field: FieldType,
  value: unknown,
  onChange: (value: unknown) => void,
  onBlur: () => void,
): React.ReactNode => {
  if (field.field_type === "SELECT") {
    const hasDatabase =
      field.configuration?.options_root_table &&
      field.configuration?.options_root_column;
    const databaseConfig = hasDatabase
      ? {
          table: field.configuration?.options_root_table,
          column: field.configuration?.options_root_column,
        }
      : undefined;

    return (
      <DataCenterDataSelect
        key={field.id}
        placeholder={
          field.configuration?.placeholder || "Seleccione una opción"
        }
        enableSearch
        initOptions={field.configuration?.options || []}
        database={databaseConfig}
        name={field.name}
        onChange={(selected) => {
          onChange(selected);
          onBlur();
        }}
        value={
          typeof value === "string"
            ? value
            : (value as { key: string; label: string } | null)
        }
      />
    );
  }

  if (field.field_type === "MULTISELECT") {
    const hasDatabase =
      field.configuration?.options_root_table &&
      field.configuration?.options_root_column;
    const databaseConfig = hasDatabase
      ? {
          table: field.configuration?.options_root_table,
          column: field.configuration?.options_root_column,
        }
      : undefined;

    return (
      <DataCenterDataMultiselect
        key={field.id}
        enableSearch
        initOptions={field.configuration?.options || []}
        className="w-full"
        placeholder={
          field.configuration?.placeholder || "Seleccione una opción"
        }
        database={databaseConfig}
        value={(value as Array<{ key: string; label: string }>) || []}
        onChange={(value) => onChange(value)}
        onBlur={onBlur}
      />
    );
  }

  if (field.field_type === "URL") {
    const allowedExtensions =
      field.configuration?.allowedFileTypes &&
      field.configuration?.allowedFileTypes.length > 0
        ? field.configuration?.allowedFileTypes
        : ["*"];
    return (
      <FileInput
        value={value as File[] | File | null}
        onChange={(files) => {
          onChange(files);
          onBlur();
        }}
        allowMultiples={field?.configuration?.allowMultipleFiles || false}
        allowed_mimes={allowedExtensions}
        maxSizeBytes={
          field.configuration?.maxFileSize &&
          typeof field.configuration.maxFileSize === "number"
            ? field.configuration.maxFileSize * 1024 * 1024
            : undefined
        }
      />
    );
  }

  if (field.field_type === "BOOLEAN") {
    const labels = field.field_type_label?.split("/");
    return (
      <div className="flex gap-2 items-center">
        <Switch
          id={field.name}
          checked={Boolean(value)}
          onCheckedChange={(checked) => {
            onChange(checked);
            onBlur();
          }}
        />
        <span className="text-body text-size-body-sm">
          {value ? labels?.[0] || "Sí" : labels?.[1] || "No"}
        </span>
      </div>
    );
  }
  if (field.field_type === "TEXTAREA") {
    return (
      <Textarea
        maxLength={
          field?.configuration?.maxLength
            ? Number(field.configuration.maxLength)
            : undefined
        }
        className="bg-neutral-object-accent-silent border-neutral"
        required={field.is_required}
        placeholder={field.configuration?.placeholder || "Ingrese un valor"}
        value={(value as string) || ""}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
      />
    );
  }
  if (field.field_type === "DATE" || field.field_type === "DATETIME") {
    const isRange = field.configuration?.type === "date_range";
    if (isRange) {
      const rangeValue = value as { start?: string; end?: string } | undefined;
      return (
        <DateRangeInput
          key={field.id}
          minDate={field?.configuration?.minDate}
          maxDate={field.configuration?.maxDate}
          includeTime={field.configuration?.includeTime ?? false}
          format24={field.configuration?.format24 ?? false}
          placeholder={
            field.configuration?.placeholder || "Selecciona una fecha"
          }
          value={{
            start: rangeValue?.start || "",
            end: rangeValue?.end || "",
          }}
          onChange={(dateRange) => {
            onChange(dateRange);
          }}
          onBlur={onBlur}
        />
      );
    }
    return (
      <DateInput
        key={field.id}
        minDate={field?.configuration?.minDate}
        maxDate={field.configuration?.maxDate}
        includeTime={field.configuration?.includeTime ?? false}
        format24={field.configuration?.format24 ?? false}
        placeholder={field.configuration?.placeholder || "Seleccione una fecha"}
        value={(value as string) || ""}
        onChange={(date) => {
          onChange(date);
          onBlur();
        }}
      />
    );
  }
  if (field.field_type === "NUMBER") {
    return (
      <Input
        maxLength={
          field?.configuration?.maxLength
            ? Number(field.configuration.maxLength)
            : undefined
        }
        className="w-full"
        required={field.is_required}
        placeholder={field.configuration?.placeholder || "Ingrese un número"}
        value={(value as string) || ""}
        onChange={(e) => {
          const value = e.target.value;
          const numericValue = formatNumberInput(value, true, false);
          onChange(numericValue);
        }}
        onBlur={onBlur}
      />
    );
  }
  if (field.field_type === "EMAIL") {
    return (
      <Input
        maxLength={
          field?.configuration?.maxLength
            ? Number(field.configuration.maxLength)
            : undefined
        }
        className="w-full"
        required={field.is_required}
        placeholder={
          field.configuration?.placeholder || "Ingrese un correo electrónico"
        }
        value={(value as string) || ""}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
      />
    );
  }

  if (
    field.field_type === "TEXT" &&
    field.configuration?.type === "identification"
  ) {
    const identificationValue =
      (value as { documentType?: string; value?: string }) || {};
    const currentDocType =
      identificationValue.documentType ??
      getDocumentType(field.configuration?.documentType);
    const currentDocNumber = identificationValue.value || "";
    return (
      <IdentificationField
        documentTypeConfig={
          field?.configuration?.documentType as IdentificationFieldType
        }
        documentType={currentDocType as IdentificationFieldType}
        documentNumber={currentDocNumber}
        onDocumentTypeChange={(docType) => {
          onChange({
            documentType: docType,
            value: "",
          });
        }}
        onDocumentNumberChange={(docNumber) => {
          onChange({
            documentType: currentDocType,
            value: docNumber,
          });
        }}
      />
    );
  }

  if (field.field_type === "PHONE") {
    return (
      <InputPhone
        className="w-full"
        required={field.is_required}
        placeholder={
          field.configuration?.placeholder || "Ingrese un número de teléfono"
        }
        value={(value as string) || ""}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
      />
    );
  }

  return (
    <Input
      maxLength={
        field?.configuration?.maxLength
          ? Number(field.configuration.maxLength)
          : undefined
      }
      className="w-full"
      required={field.is_required}
      placeholder={field.configuration?.placeholder || "Ingrese un valor"}
      value={(value as string) || ""}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
    />
  );
};
